/**
 * Button shared logic — hook, content renderer, and styles.
 *
 * Strictly typed: zero `any`, zero `as` casts, zero non-null assertions.
 */
import { ActivityIndicator, type StyleProp, View, type ViewStyle } from "react-native";
import { type ReactNode, useMemo, useState } from "react";
import { Text, type TextSize } from "../Text";
import "../../theme"; // module augmentation for react-native-unistyles types
import { StyleSheet, useUnistyles } from "react-native-unistyles";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ButtonVariant = "primary" | "secondary" | "link";

export type ButtonVariantColor =
  | "blue"
  | "grey"
  | "danger"
  | "success"
  | "warning"
  | "red"
  | "navy"
  | "cyan"
  | "purple"
  | "teal"
  | "indigo";

export type ButtonSize = "sm" | "md" | "lg";

/**
 * Props accepted by the Button component.
 *
 * `onPress` is typed as a no-arg callback — alarm control buttons never
 * forward event args. Consumers that need the event can wrap externally.
 */
export interface ButtonProps {
  readonly label?: string;
  readonly size?: ButtonSize;
  readonly onPress?: () => void;
  readonly iconRight?: ReactNode;
  readonly iconLeft?: ReactNode;
  readonly disabled?: boolean;
  readonly variant?: ButtonVariant;
  readonly variantColor?: ButtonVariantColor;
  readonly style?: StyleProp<ViewStyle>;
  readonly textStyle?: TextSize;
  readonly uppercase?: boolean;
  readonly textColor?: string;
  readonly loading?: boolean;
  readonly loadingText?: string;
  /** Forward a testID / accessibilityLabel to the underlying Pressable. */
  readonly accessibilityLabel?: string;
  readonly testID?: string;
}

// ---------------------------------------------------------------------------
// Shared state returned by the hook
// ---------------------------------------------------------------------------

export interface ButtonSharedState {
  readonly isFocused: boolean;
  readonly setIsFocused: (value: boolean) => void;
  readonly label: string;
  readonly onPress: (() => void) | undefined;
  readonly baseStyles: (StyleProp<ViewStyle> | false | undefined)[];
  readonly showLoadingText: string | false | undefined;
  readonly showSpinner: boolean;
  readonly showChildren: boolean;
  readonly iconLeft: ReactNode | undefined;
  readonly iconRight: ReactNode | undefined;
  readonly textStyle: TextSize;
  readonly textColor: string | undefined;
  readonly uppercase: boolean;
  readonly loadingText: string | undefined;
  readonly disabled: boolean;
  readonly loading: boolean;
  readonly variant: ButtonVariant;
  readonly textStyles: Readonly<{ color: string | undefined }>;
  readonly themeSpacingSm: number;
  readonly focusBorderColor: string;
  readonly accessibilityLabel: string | undefined;
  readonly testID: string | undefined;
}

// ---------------------------------------------------------------------------
// Color variant resolution — exhaustive, no `as` cast
// ---------------------------------------------------------------------------

interface ColorTriple {
  readonly backgroundColor: string;
  readonly borderColor: string;
  readonly textColor: string;
}

/**
 * Resolve the color triple for a given variantColor.
 * Handles "blue" and "grey" as special cases; all other named colors map to
 * theme palette shades.
 */
function resolveColorVariant(
  variantColor: ButtonVariantColor,
  variant: ButtonVariant,
  theme: ReturnType<typeof useUnistyles>["theme"],
): ColorTriple {
  if (variantColor === "blue") {
    return {
      backgroundColor: theme.colors.interactive.buttons.default_background,
      borderColor: theme.colors.interactive.buttons.default_background,
      textColor:
        variant === "primary" ?
          theme.colors.interactive.buttons.default_foreground
        : theme.colors.interactive.buttons.default_background,
    };
  }

  if (variantColor === "grey") {
    return {
      backgroundColor: theme.colors.foreground,
      borderColor: theme.colors.border,
      textColor: variant === "primary" ? theme.colors.text : theme.colors.textMuted,
    };
  }

  // All remaining named colors map to a 600-shade from the palette
  const namedColorMap = {
    danger: theme.colors.status.error,
    success: theme.colors.status.success,
    warning: theme.colors.status.warning,
    red: theme.colors.red[600],
    navy: theme.colors.navy[600],
    cyan: theme.colors.cyan[600],
    purple: theme.colors.purple[600],
    teal: theme.colors.teal[600],
    indigo: theme.colors.indigo[600],
  } satisfies Record<Exclude<ButtonVariantColor, "blue" | "grey">, string>;

  const mappedColor: string = namedColorMap[variantColor];
  return {
    backgroundColor: mappedColor,
    borderColor: mappedColor,
    textColor: variant === "primary" ? theme.colors.interactive.buttons.default_foreground : mappedColor,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useButtonSharedState({
  label = "Base Button",
  size = "sm",
  iconRight,
  iconLeft,
  disabled = false,
  variant = "primary",
  variantColor = "blue",
  onPress,
  style,
  textStyle = "sm/regular",
  textColor,
  uppercase = false,
  loading = false,
  loadingText,
  accessibilityLabel,
  testID,
}: ButtonProps): ButtonSharedState {
  const { theme } = useUnistyles();
  const [isFocused, setIsFocused] = useState(false);
  const showLoadingText = loading && loadingText;
  const showSpinner = loading;
  const showChildren = !loading || Boolean(showLoadingText);

  const colorVariant = useMemo(() => resolveColorVariant(variantColor, variant, theme), [variantColor, theme, variant]);

  const buttonStyles = useMemo((): ViewStyle => {
    if (variant === "primary") {
      return {
        backgroundColor: colorVariant.backgroundColor,
        borderColor: colorVariant.borderColor,
        borderWidth: 1,
      };
    }
    if (variant === "secondary") {
      return {
        backgroundColor: "transparent",
        borderColor: colorVariant.borderColor,
        borderWidth: 1,
      };
    }
    // variant === "link"
    return { backgroundColor: "transparent" };
  }, [variant, colorVariant]);

  const buttonSizeStyles = useMemo((): ViewStyle | undefined => {
    if (size === "md") return styles.buttonMd;
    if (size === "lg") return styles.buttonLg;
    return undefined;
  }, [size]);

  const textStyles = useMemo(
    (): Readonly<{ color: string | undefined }> => ({
      // All variants (primary, secondary, link) derive text color from the
      // resolved color variant. Custom textColor is applied later in
      // ButtonContent via an inline style override.
      color: colorVariant.textColor,
    }),
    [colorVariant],
  );

  return {
    isFocused,
    setIsFocused,
    label,
    onPress,
    baseStyles: [
      styles.button,
      buttonSizeStyles,
      buttonStyles,
      (disabled || loading) && variant !== "link" && styles.buttonDisabled,
      (disabled || loading) && variant === "secondary" && styles.buttonSecondaryDisabled,
      style,
    ],
    showLoadingText,
    showSpinner,
    showChildren,
    iconLeft,
    iconRight,
    textStyle,
    textColor,
    uppercase,
    loadingText,
    disabled,
    loading,
    variant,
    textStyles,
    themeSpacingSm: theme.spacing.sm,
    focusBorderColor: theme.colors.interactive.buttons.focused_border,
    accessibilityLabel,
    testID,
  };
}

// ---------------------------------------------------------------------------
// ButtonContent — pure renderer, no side effects
// ---------------------------------------------------------------------------

export interface ButtonContentProps {
  readonly iconLeft: ReactNode | undefined;
  readonly iconRight: ReactNode | undefined;
  readonly showChildren: boolean;
  readonly showSpinner: boolean;
  readonly loadingText: string | undefined;
  readonly textStyles: Readonly<{ color: string | undefined }>;
  readonly textStyle: TextSize;
  readonly textColor: string | undefined;
  readonly uppercase: boolean;
  readonly label: string;
  readonly themeSpacingSm: number;
  readonly showLoadingText: string | false | undefined;
}

export function ButtonContent({
  iconLeft,
  iconRight,
  showChildren,
  showSpinner,
  loadingText,
  textStyles,
  textStyle,
  textColor,
  uppercase,
  label,
  themeSpacingSm,
  showLoadingText,
}: ButtonContentProps) {
  return (
    <>
      {iconLeft !== undefined && iconLeft !== null && showChildren && <View style={styles.iconLeft}>{iconLeft}</View>}
      {showSpinner && (
        <ActivityIndicator
          size="small"
          color={textStyles.color}
          style={loadingText ? { marginRight: themeSpacingSm } : undefined}
        />
      )}
      {showChildren && (
        <Text
          textStyle={textStyle}
          style={[textStyles, textColor ? { color: textColor } : undefined]}
          textTransform={uppercase ? "uppercase" : "none"}
        >
          {showLoadingText !== false && showLoadingText !== undefined ? loadingText : label}
        </Text>
      )}
      {iconRight !== undefined && iconRight !== null && showChildren && (
        <View style={styles.iconRight}>{iconRight}</View>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

export const styles = StyleSheet.create((theme) => ({
  button: {
    paddingHorizontal: theme.spacing["2xl"],
    paddingVertical: theme.spacing.md,
    borderRadius: 8,
    borderCurve: "continuous",
    minWidth: 80,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
  buttonLg: {
    height: 48,
    minWidth: 100,
  },
  buttonMd: {
    height: 44,
  },
  iconLeft: {
    marginRight: theme.spacing.lg,
  },
  iconRight: {
    marginLeft: theme.spacing.lg,
  },
  buttonDisabled: {
    backgroundColor: theme.colors.interactive.buttons.disabled_background,
    borderColor: theme.colors.interactive.buttons.disabled_background,
    borderWidth: 1,
    opacity: 0.7,
  },
  buttonSecondaryDisabled: {
    backgroundColor: "transparent",
    borderColor: theme.colors.border,
    borderWidth: 1,
    opacity: 0.7,
  },
}));
